// AIM orb (the week) — the five ORDER rows. Spec: docs/redesign/ORB_SPEC.md.
// Goal / Endeavour reuse this layout with that stage's spine text as the objective.
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { useV10 } from '../theme';
import { STAGE_LABELS } from '../../domain/olmps';
import { DOW, clock, clockLong, dateKeyOf, keyToDate, addDaysKey, trunc } from './time';
import { Btn, Head, Line, Mark, PromptLine, Quiet, Txt, useK } from './ui';
import { NOT_SET, ObjectiveWidget, aimsOf, capacity, chainOf, eventsFor, goalName, joinLine, moveTarget, slotState } from './shared';
import { itemsForSector } from './data';
import type { OrbItem, OrbLevel, RowCtx, RowSet } from './types';

type DayState = 'green' | 'amber' | 'red' | 'none';

function Tile({ label, state, sub, today, onPress }: { label: string; state: DayState; sub: string; today: boolean; onPress: () => void }) {
  const t = useV10();
  const k = useK();
  const fill = state === 'green' ? t.onPace : state === 'amber' ? t.behind : state === 'red' ? t.lapsed : 'transparent';
  return (
    <Pressable onPress={onPress} style={{ alignItems: 'center', gap: 2 * k }}>
      <View
        style={{
          width: 30 * k,
          height: 30 * k,
          borderRadius: 8 * k,
          backgroundColor: fill,
          borderWidth: today ? 2 : 1,
          borderColor: today ? t.goldBright : state === 'none' ? t.navyLine : fill,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ color: state === 'none' ? t.ivorySoft : t.onGold, fontSize: 9.5 * k, fontWeight: '800' }}>{sub}</Text>
      </View>
      <Text style={{ color: t.ivorySoft, fontSize: 9.5 * k, fontWeight: '700' }}>{label}</Text>
    </Pressable>
  );
}

export function aimRows(c: RowCtx, stage: OrbLevel): RowSet {
  const { data, today, now, sector, spine, acts, notes } = c;
  const todayKey = dateKeyOf(now);
  const node = sector && spine ? spine[sector] : null;
  const aims = aimsOf(node);
  const week = itemsForSector(data.items, sector);
  const open = week.filter((i) => !i.completed);
  const weekKey = data.weekStartKey;

  // ---- O · objective ------------------------------------------------------
  const noteKey = `${stage}:${weekKey}:${sector ?? '-'}`;
  const note = notes.get(noteKey);
  const derived =
    stage === 'E' ? node?.endeavour ?? null : stage === 'G' ? goalName(sector, node) : aims.slice(0, 2).map((a) => a.label).join(' · ') || null;
  const objective = note?.text || derived || null;
  const serves = stage === 'A' ? goalName(sector, node) : stage === 'G' ? node?.endeavour ?? null : null;
  const cw = today.ladder.rungs.aim.currentWindow;
  const pace = stage === 'A' && week.length ? (cw.count >= cw.needed ? 'ordered' : cw.onTrack ? 'on pace' : 'behind') : null;
  const oLine = objective ? joinLine([objective, serves && `serves ${serves}`, pace]) : `Set this ${stage === 'A' ? 'week’s aim' : STAGE_LABELS[stage].toLowerCase()} in ATLAS`;

  // ---- R · ready ----------------------------------------------------------
  const slots = week.map((i) => ({ i, s: slotState(i, eventsFor(c, i.date)) }));
  const ready = slots.filter((x) => x.s.ready).length;
  const block = slots.find((x) => !x.s.ready);
  const rLine = week.length ? joinLine([`${ready} of ${week.length} priorities ready`, block && `${trunc(block.i.title, 16)}: ${block.s.why}`]) : 'No priorities yet · pick them in KYRS';
  const cap = capacity(c, open);
  const rShow = [...slots.filter((x) => !x.s.ready), ...slots.filter((x) => x.s.ready)].slice(0, 4);

  // ---- D · plan -----------------------------------------------------------
  const dLine = week.length
    ? joinLine([...week.slice(0, 2).map((i) => `${DOW[keyToDate(i.date).getDay()]}: ${trunc(i.title, 14)}`), week.length > 2 && `(+${week.length - 2})`])
    : 'No plan yet · choose the week’s priorities in KYRS';

  // ---- E · days -----------------------------------------------------------
  const status = (key: string): DayState => {
    if (key > todayKey) return 'none';
    const L = data.ledger[key];
    if (L && L.rowsGreen >= 5) return 'green';
    const day = week.filter((i) => i.date === key);
    if (!day.length) return L && L.rowsGreen >= 1 ? 'amber' : 'none';
    const done = day.filter((i) => i.completed).length;
    if (done === day.length) return 'green';
    if (done > 0 || (L && L.rowsGreen >= 1)) return 'amber';
    return key === todayKey ? 'none' : 'red';
  };
  const days = data.weekDays.map((d) => ({ ...d, st: status(d.key), items: week.filter((i) => i.date === d.key) }));
  const g = days.filter((d) => d.st === 'green').length;
  const a = days.filter((d) => d.st === 'amber').length;
  const r = days.filter((d) => d.st === 'red').length;
  const x = days.filter((d) => d.key <= todayKey).length;
  const tally = [g > 0 && `${g} green`, a > 0 && `${a} amber`, r > 0 && `${r} red`].filter(Boolean).join(', ');
  const eLine = joinLine([`Day ${x} of ${days.length}`, tally || 'nothing graded yet']);

  // ---- R · review ---------------------------------------------------------
  const closeAt = keyToDate(data.weekEndKey);
  const [eh, em] = data.game.eveningSessionTime.split(':').map(Number);
  closeAt.setHours(eh, em, 0, 0);
  const after = now >= closeAt;
  const lesson = notes.get(`L:week:${weekKey}`)?.text;
  const doneN = week.filter((i) => i.completed).length;
  const fLine = after ? joinLine([`${doneN} of ${week.length}`, lesson ?? 'add the lesson']) : `Review ${DOW[closeAt.getDay()]} ${clockLong(closeAt)}`;

  const nextWeek = (i: OrbItem) => addDaysKey(i.date, 7);

  // ---- full answers (read top to bottom) ----------------------------------
  const scale = stage === 'A' ? 'week' : stage === 'G' ? 'Goal' : 'Endeavour';
  const chain = stage === 'A' ? chainOf([['Goal', goalName(sector, node)], ['Endeavour', node?.endeavour]]) : stage === 'G' ? chainOf([['Endeavour', node?.endeavour]]) : null;
  const oAnswer = [
    `Objective: ${objective ?? NOT_SET}`,
    ...(stage === 'E' ? [] : [`Supports: ${chain ?? NOT_SET}`]),
    `Done when: ${stage === 'A' && week.length ? `all ${week.length} priorities this week are done (${doneN} so far)` : NOT_SET}${pace ? ` · ${pace}` : ''}`,
  ];
  const blocks = slots.filter((x) => !x.s.ready);
  const rAnswer = week.length
    ? [
        blocks.length ? `NOT READY — ${ready} of ${week.length} priorities ready` : `READY — all ${week.length} priorities have a clear slot`,
        `Time: ${cap.freeH}h open · ${cap.needH}h needed${cap.needH > cap.freeH ? ' — more than fits' : ''}`,
        ...blocks.slice(0, 2).map((x) => `✗ ${DOW[keyToDate(x.i.date).getDay()]} ${x.i.title}: ${x.s.why}`),
      ]
    : [`Readiness: ${NOT_SET} — no priorities this week`];
  const dAnswer = week.length
    ? [...week.slice(0, 4).map((i) => `${DOW[keyToDate(i.date).getDay()]}: ${i.title}${i.completed ? ' ✓' : ''}`), ...(week.length > 4 ? [`+${week.length - 4} more`] : [])]
    : [`Plan: ${NOT_SET} — no priorities on days`];
  const eAnswer = [`Day ${x} of ${days.length}`, `Days: ${tally || 'nothing graded yet'}`, `Progress: ${doneN} of ${week.length} priorities done`];
  const carry = open.map((i) => i.title);
  const fAnswer = [
    `Outcome: ${week.length ? `${doneN} of ${week.length} done` : 'nothing was planned'}${after ? '' : ` · review ${DOW[closeAt.getDay()]} ${clockLong(closeAt)}`}`,
    `Carries forward: ${carry.length ? carry.slice(0, 3).join(', ') + (carry.length > 3 ? ` (+${carry.length - 3})` : '') : 'nothing'}`,
    `Change: ${lesson ?? NOT_SET}`,
  ];

  return {
    orient: {
      question: `Why this ${scale}, and what counts as done?`,
      line: oLine,
      answer: oAnswer,
      tone: objective ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'atlas', label: 'ATLAS' },
      widget: (
        <ObjectiveWidget
          key={noteKey}
          text={objective}
          serves={serves ? `${serves}${pace ? ` · ${pace}` : ''}` : pace}
          state={note?.state}
          empty="No aim set for this sector yet."
          onAccept={() => notes.set(noteKey, { ...note, text: note?.text, state: 'accepted' })}
          onCorrect={(text) => notes.set(noteKey, { text, state: 'corrected' })}
          onChange={() => acts.go({ kind: 'tool', tool: 'atlas', label: 'ATLAS' })}
        />
      ),
    },
    regulate: {
      question: `Do I have everything this ${scale} needs?`,
      line: rLine,
      answer: rAnswer,
      tone: block ? 'warn' : week.length ? 'ok' : 'quiet',
      swipe: { kind: 'tool', tool: 'krnos', view: 'weekly', label: 'KRNOS week' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 3 }}>
          <Txt soft style={{ textAlign: 'center' }}>
            Open {cap.freeH}h · needs {cap.needH}h{cap.needH > cap.freeH ? ' — more than fits' : ''}
          </Txt>
          {rShow.length ? (
            rShow.map(({ i, s }) => (
              <Line key={i.key} right={!s.ready ? <Btn label="Move" disabled={acts.busy} onPress={() => void acts.move(i, moveTarget(i, todayKey))} /> : undefined}>
                <Mark ok={s.ready} />
                <Txt lines={1} style={{ flex: 1 }}>
                  {DOW[keyToDate(i.date).getDay()]} · {i.title}
                  {!s.ready && s.why ? ` — ${s.why}` : ''}
                </Txt>
              </Line>
            ))
          ) : (
            <Quiet>Nothing planned this week yet.</Quiet>
          )}
          {week.length > rShow.length ? <Txt faint style={{ textAlign: 'center' }}>+{week.length - rShow.length} more</Txt> : null}
        </View>
      ),
    },
    direct: {
      question: `What’s the plan for this ${scale}?`,
      line: dLine,
      answer: dAnswer,
      tone: week.length ? undefined : 'quiet',
      swipe: { kind: 'tool', tool: 'kyrs', label: 'KYRS' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 3 }}>
          {week.length ? (
            week.slice(0, 5).map((i, idx) => {
              const prev = week[idx - 1];
              const nxt = week[idx + 1];
              const up = prev && prev.date !== i.date ? prev : null;
              const dn = nxt && nxt.date !== i.date ? nxt : null;
              return (
                <Line
                  key={i.key}
                  right={
                    <View style={{ flexDirection: 'row', gap: 3 }}>
                      <Btn label="▲" disabled={!up || acts.busy} onPress={() => up && void acts.swap(i, up, 'day')} />
                      <Btn label="▼" disabled={!dn || acts.busy} onPress={() => dn && void acts.swap(i, dn, 'day')} />
                    </View>
                  }
                >
                  <Txt faint w={30}>{DOW[keyToDate(i.date).getDay()]}</Txt>
                  <Txt lines={1} style={{ flex: 1, textDecorationLine: i.completed ? 'line-through' : 'none' }}>{i.title}</Txt>
                </Line>
              );
            })
          ) : (
            <Quiet>No priorities placed on days yet.</Quiet>
          )}
          {week.length > 5 ? <Txt faint style={{ textAlign: 'center' }}>+{week.length - 5} more</Txt> : null}
        </View>
      ),
    },
    execute: {
      question: 'What’s happening now?',
      line: eLine,
      answer: eAnswer,
      tone: r > 0 ? 'warn' : g > 0 ? 'ok' : 'quiet',
      swipe: { kind: 'drill', level: 'T', dayKey: todayKey, tool: 'krnos', label: 'today’s orb' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 6, alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-around', alignSelf: 'stretch' }}>
            {days.map((d) => {
              const done = d.items.filter((i) => i.completed).length;
              return <Tile key={d.key} label={DOW[d.date.getDay()]} state={d.st} today={d.key === todayKey} sub={d.items.length ? `${done}/${d.items.length}` : '–'} onPress={() => acts.drillDay(d.key)} />;
            })}
          </View>
          <Txt faint style={{ textAlign: 'center' }}>Tap a day to open its Today orb</Txt>
        </View>
      ),
    },
    revise: {
      question: 'What happened, and what needs to change?',
      line: fLine,
      answer: fAnswer,
      tone: 'quiet',
      swipe: { kind: 'session', session: 'night', tool: 'clio', label: 'CLIO weekly session' },
      widget: (
        <View style={{ alignSelf: 'stretch', gap: 4 }}>
          {week.length ? (
            week.slice(0, 3).map((i) => {
              const partly = !i.completed && i.missed === 'Partly done';
              return (
                <Line
                  key={i.key}
                  right={
                    <View style={{ flexDirection: 'row', gap: 3 }}>
                      <Btn label="Done" tone={i.completed ? 'ok' : 'ghost'} disabled={acts.busy} onPress={() => void acts.done(i)} />
                      <Btn label="Part" tone={partly ? 'warn' : 'ghost'} disabled={acts.busy} onPress={() => void acts.missed(i, 'Partly done')} />
                      <Btn label="Move" disabled={acts.busy} onPress={() => void acts.move(i, nextWeek(i))} />
                    </View>
                  }
                >
                  <Txt lines={1} style={{ flex: 1 }}>{i.title}</Txt>
                </Line>
              );
            })
          ) : (
            <Quiet>Nothing was planned this week.</Quiet>
          )}
          <PromptLine prompt="What changes next week?" saved={lesson} onSave={(t) => acts.lesson('week', weekKey, t)} />
        </View>
      ),
    },
  };
}
